// SB6-ANA-FILT-06 (S1, Track B) — generic faceted-filter TABLE primitive.
//
// The AI Filter Module (Track C) and the Defect Filter Widget (Track B)
// are structurally identical: a checkbox column + master checkbox + a
// name column + a count column + ONE parameterised "extra" column, all
// under a header that carries a "Filter active" MUI Switch and a slot
// reserved for the Track-D save/load preset controls.
//
// This component owns NONE of the data fetching or the global-filter
// wiring — it is a pure presentational table parameterised by:
//
//   * `rows`        — the row model (generic `R`; each row needs a stable
//                     `id`, a localized `name`, and a numeric `count`),
//   * `extraColumn` — an optional column definition (header label + a
//                     per-row render slot). Pass `null`/omit to hide the
//                     column entirely (e.g. the Defect widget hides the
//                     severity column when `hasClassModel` is false, D10),
//   * `checkedIds`  — the controlled selection (the parent maps this onto
//                     the global `defectTypeIds` / AI sub-filter state),
//   * `active` / `onActiveChange` — the "Filter active" header switch
//                     (D4 two-way sync with the L3 option button), and
//   * `headerSlot`  — a render slot in the header reserved for Track D
//                     (named save/load presets); empty for now.
//
// The master checkbox checks/unchecks all VISIBLE rows (S1); when the
// visible rows are a mix of checked/unchecked it shows the indeterminate
// state. Counts are right-aligned tabular figures.
import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

export interface FacetRow {
  /** Stable row identity (e.g. catalogue entry id / AI class uuid). */
  id: string;
  /** Display label for the name column. */
  name: string;
  /** The count shown in the count column. */
  count: number;
}

export interface FacetExtraColumn<R extends FacetRow> {
  /** Header text for the extra column. */
  header: string;
  /** Per-row cell renderer for the extra column. */
  render: (row: R) => React.ReactNode;
  /** Optional fixed width for the extra column. */
  width?: number | string;
  /** Header `align` for the extra column (default `left`). */
  align?: "left" | "center" | "right";
}

export interface FacetTableWidgetProps<R extends FacetRow> {
  /** A11y region label (each consumer supplies its own widget name). */
  ariaLabel: string;
  /** `data-testid` root for the table + its controls. */
  testId: string;
  /** The rows to render (already filtered/sorted by the consumer). */
  rows: readonly R[];
  /** Header label for the count column (e.g. "# defects"). */
  countHeader: string;
  /** Header label for the name column (e.g. "Defect type"). */
  nameHeader: string;
  /** The extra column; pass `null`/omit to hide it (D10). */
  extraColumn?: FacetExtraColumn<R> | null;
  /** Controlled set of checked row ids. */
  checkedIds: ReadonlySet<string>;
  /** Toggle a single row's checked state. */
  onToggleRow: (id: string, checked: boolean) => void;
  /**
   * Master checkbox: `checked` = every visible row checked. The handler
   * receives the desired state for ALL currently-visible rows.
   */
  onToggleAll: (checked: boolean) => void;
  /** The "Filter active" header switch state (D4). */
  active: boolean;
  /** Fires when the user flips the "Filter active" switch. */
  onActiveChange: (active: boolean) => void;
  /** Header slot reserved for Track-D save/load preset controls. */
  headerSlot?: React.ReactNode;
  /** Optional toolbar row above the table (e.g. "show all" toggle). */
  toolbar?: React.ReactNode;
  /** Shown in place of the table body when there are no rows. */
  emptyMessage?: React.ReactNode;
}

/**
 * Generic faceted-filter table. See the file header for the parameters
 * and the master-checkbox / active-switch semantics.
 */
export function FacetTableWidget<R extends FacetRow>({
  ariaLabel,
  testId,
  rows,
  countHeader,
  nameHeader,
  extraColumn = null,
  checkedIds,
  onToggleRow,
  onToggleAll,
  active,
  onActiveChange,
  headerSlot,
  toolbar,
  emptyMessage,
}: FacetTableWidgetProps<R>): React.ReactElement {
  const visibleCount = rows.length;
  const checkedVisible = rows.filter((r) => checkedIds.has(r.id)).length;
  const allChecked = visibleCount > 0 && checkedVisible === visibleCount;
  const someChecked = checkedVisible > 0 && checkedVisible < visibleCount;
  // Number of body columns (checkbox + name + count [+ extra]).
  const colSpan = 3 + (extraColumn ? 1 : 0);

  return (
    <Box
      data-testid={testId}
      role="region"
      aria-label={ariaLabel}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        p: 1.5,
        gap: 1,
      }}
    >
      {/* Header — "Filter active" switch on the left, save/load slot right. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={active}
              onChange={(e) => {
                onActiveChange(e.target.checked);
              }}
              inputProps={{
                // Stable id-less hook for tests + a11y.
                "aria-label": "Filter active",
              }}
              data-testid={`${testId}-active`}
            />
          }
          label={
            <Typography variant="body2" component="span">
              Filter active
            </Typography>
          }
          sx={{ m: 0 }}
        />
        <Box
          data-testid={`${testId}-header-slot`}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          {headerSlot}
        </Box>
      </Stack>

      {toolbar ? <Box>{toolbar}</Box> : null}

      <TableContainer
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        <Table size="small" stickyHeader aria-label={ariaLabel}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allChecked}
                  indeterminate={someChecked}
                  disabled={visibleCount === 0}
                  onChange={(e) => {
                    onToggleAll(e.target.checked);
                  }}
                  inputProps={{ "aria-label": "Select all visible rows" }}
                  data-testid={`${testId}-master`}
                />
              </TableCell>
              <TableCell>{nameHeader}</TableCell>
              <TableCell align="right">{countHeader}</TableCell>
              {extraColumn ? (
                <TableCell
                  align={extraColumn.align ?? "left"}
                  {...(extraColumn.width !== undefined
                    ? { sx: { width: extraColumn.width } }
                    : {})}
                >
                  {extraColumn.header}
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleCount === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  align="center"
                  sx={{ color: "text.secondary", py: 3 }}
                  data-testid={`${testId}-empty`}
                >
                  {emptyMessage ?? "No rows."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const checked = checkedIds.has(row.id);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    data-testid={`${testId}-row-${row.id}`}
                    data-checked={checked ? "true" : "false"}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={(e) => {
                          onToggleRow(row.id, e.target.checked);
                        }}
                        inputProps={{ "aria-label": `Select ${row.name}` }}
                        data-testid={`${testId}-check-${row.id}`}
                      />
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                      data-testid={`${testId}-count-${row.id}`}
                    >
                      {row.count.toString()}
                    </TableCell>
                    {extraColumn ? (
                      <TableCell align={extraColumn.align ?? "left"}>
                        {extraColumn.render(row)}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
